import React, { useEffect, useRef, useState } from "react"
import { useKeyboardOpen } from "@/hooks/useKeyboardOpen"

export function Hearts() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    let W = window.innerWidth
    let H = window.innerHeight
    canvas.width = W
    canvas.height = H

    let animationFrameId: number

    class Heart {
      x: number
      y: number
      size: number
      baseSize: number
      maxScale: number
      color: string
      opacity: number
      spawnTime: number
      duration: number

      constructor() {
        this.x = Math.random() * W
        this.y = Math.random() * H
        this.baseSize = Math.random() * 10 + 5
        this.size = 0
        this.maxScale = Math.random() * 0.5 + 0.5 // Scale between 0.5 and 1.0

        const reds = [255, 255, 240, 220]
        const pinks = [192, 182, 105, 50]
        const r = reds[Math.floor(Math.random() * reds.length)]
        const g = pinks[Math.floor(Math.random() * pinks.length)]
        const b = pinks[Math.floor(Math.random() * pinks.length)]

        this.opacity = 0
        this.color = `rgba(${r}, ${g}, ${b}, `
        this.duration = 1700 // matches codepen total duration
        this.spawnTime = Date.now() - Math.random() * this.duration
      }

      update() {
        const elapsed = Date.now() - this.spawnTime
        const progress = elapsed / this.duration

        if (progress >= 1) {
          // Reset heart
          this.x = Math.random() * W
          this.y = Math.random() * H
          this.spawnTime = Date.now()
          return
        }

        // Pulse logic from CodePen:
        // Phase 1 (0-0.7s): Scale 0 to mid
        // Phase 2 (0.7-1.4s): Scale mid to 1
        // Phase 3 (1.4-1.7s): Scale 1, Fade out

        if (progress < 0.4) {
          // Pop in
          const p = progress / 0.4
          this.size = this.baseSize * this.maxScale * 0.7 * p
          this.opacity = p * 0.6
        } else if (progress < 0.8) {
          // Expand
          const p = (progress - 0.4) / 0.4
          this.size = this.baseSize * this.maxScale * (0.7 + 0.3 * p)
          this.opacity = 0.6
        } else {
          // Fade out
          const p = (progress - 0.8) / 0.2
          this.size = this.baseSize * this.maxScale
          this.opacity = 0.6 * (1 - p)
        }
      }

      draw(ctx: CanvasRenderingContext2D) {
        if (this.opacity <= 0) return
        ctx.save()
        ctx.translate(this.x, this.y)
        ctx.beginPath()
        ctx.moveTo(0, 0)
        ctx.bezierCurveTo(
          0,
          -this.size / 2,
          -this.size,
          -this.size / 2,
          -this.size,
          0,
        )
        ctx.bezierCurveTo(
          -this.size,
          this.size,
          0,
          this.size * 1.5,
          0,
          this.size * 2,
        )
        ctx.bezierCurveTo(
          0,
          this.size * 1.5,
          this.size,
          this.size,
          this.size,
          0,
        )
        ctx.bezierCurveTo(this.size, -this.size / 2, 0, -this.size / 2, 0, 0)
        ctx.fillStyle = this.color + this.opacity + ")"
        ctx.fill()
        ctx.restore()
      }
    }

    const particleCount = window.innerWidth < 768 ? 20 : 40
    const hearts: Heart[] = []

    for (let i = 0; i < particleCount; i++) {
      hearts.push(new Heart())
    }

    const ctx = canvas.getContext("2d") as CanvasRenderingContext2D

    function animate() {
      ctx.clearRect(0, 0, canvas!.width, canvas!.height)

      for (const h of hearts) {
        h.update()
        h.draw(ctx)
      }

      animationFrameId = requestAnimationFrame(animate)
    }

    animate()

    const handleResize = () => {
      W = window.innerWidth
      H = window.innerHeight
      canvas.width = W
      canvas.height = H
    }

    window.addEventListener("resize", handleResize)

    return () => {
      cancelAnimationFrame(animationFrameId)
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className="fixed top-0 z-99999 pointer-events-none h-screen w-screen overflow-hidden"
    />
  )
}

export function ValentineLetter({ onOpen }: { onOpen: () => void }) {
  return (
    <div
      className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-100000 cursor-pointer animate-bounce group"
      onClick={onOpen}
    >
      <div className="relative transform hover:scale-110 transition-transform duration-300">
        {/* Envelope Body */}
        <svg
          width="150"
          height="100"
          viewBox="0 0 150 100"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            width="150"
            height="100"
            rx="4"
            fill="#FCE3E9"
            stroke="#FFB3C3"
            strokeWidth="2"
          />
          <path d="M0 0L75 50L150 0" stroke="#FFB3C3" strokeWidth="2" />
          {/* Heart Seal */}
          <path
            d="M75 55C75 55 72 52 70 52C68 52 66 54 66 56C66 58 68 60 75 65C82 60 84 58 84 56C84 54 82 52 80 52C78 52 75 55 75 55Z"
            fill="#FD4755"
            className="animate-pulse"
          />
        </svg>
        <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-white px-2 py-1 rounded-md shadow-sm border border-pink-100 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap text-pink-500 font-bold">
          Open Me! 💌
        </div>
      </div>
    </div>
  )
}

export function ValentinesScene() {
  const [showLetter, setShowLetter] = useState(false)
  const [isExploded, setIsExploded] = useState(false)
  const [isFading, setIsFading] = useState(false)
  const isKeyboardOpen = useKeyboardOpen()

  useEffect(() => {
    const hasSeenLetter = localStorage.getItem("valentine_letter_seen_2026")
    if (!hasSeenLetter) {
      setShowLetter(true)
    }
  }, [])

  const handleOpenLetter = () => {
    setIsExploded(true)
    localStorage.setItem("valentine_letter_seen_2026", "true")
    setTimeout(() => {
      setIsFading(true)
      setTimeout(() => {
        setIsExploded(false)
        setShowLetter(false)
      }, 1000)
    }, 2000)
  }

  return (
    <>
      <Hearts />
      {showLetter && !isKeyboardOpen && !isExploded && (
        <ValentineLetter onOpen={handleOpenLetter} />
      )}
      {isExploded && (
        <div
          className={`fixed inset-0 flex items-center justify-center z-100001 pointer-events-none transition-opacity duration-1000 ${
            isFading ? "opacity-0" : "opacity-100"
          }`}
        >
          <div className="text-center animate-bounce">
            <h1 className="text-6xl md:text-8xl font-black text-white drop-shadow-[0_5px_15px_rgba(253,71,85,0.5)] animate-pulse">
              Happy Valentine's Day!
            </h1>
            <div className="flex justify-center gap-4 mt-8">
              {["❤️", "💖", "💝", "💕", "💘"].map((emoji, i) => (
                <span
                  key={i}
                  className="text-6xl animate-ping"
                  style={{ animationDelay: `${i * 0.2}s` }}
                >
                  {emoji}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
